<!-- ══════════════════════════════════════════════════════
     privacy-policy.component.html
     ══════════════════════════════════════════════════════ -->

<div class="pp-layout">

  <!-- ── Sidebar ─────────────────────────────────────────── -->
  <aside class="pp-sidebar">
    <div class="pp-sidebar__label">Sections</div>
    <nav>
      <a routerLink="./" fragment="introduction">1. Introduction</a>
      <a routerLink="./" fragment="information-we-collect">2. Information We Collect</a>
      <a routerLink="./" fragment="how-we-use-your-information">3. How We Use It</a>
      <a routerLink="./" fragment="cookies-tracking-technologies">4. Cookies &amp; Tracking</a>
      <a routerLink="./" fragment="how-we-share-your-information">5. Sharing</a>
      <a routerLink="./" fragment="data-retention">6. Data Retention</a>
      <a routerLink="./" fragment="your-privacy-rights">7. Your Rights</a>
      <a routerLink="./" fragment="data-security">8. Data Security</a>
      <a routerLink="./" fragment="childrens-privacy">9. Children's Privacy</a>
      <a routerLink="./" fragment="georgia-specific-disclosures">10. Georgia Disclosures</a>
      <a routerLink="./" fragment="third-party-links-services">11. Third-Party Links</a>
      <a routerLink="./" fragment="changes-to-this-privacy-policy">12. Policy Changes</a>
      <a routerLink="./" fragment="contact-us-privacy-inquiries">13. Contact Us</a>
    </nav>
  </aside>

  <!-- ── Main ────────────────────────────────────────────── -->
  <main class="pp-main">
    <!-- Hero -->
    <header class="pp-hero">
      <div class="pp-hero__eyebrow">Legal Document</div>
      <h1 class="pp-hero__title">Privacy Policy</h1>
      <div class="pp-hero__meta">
        <strong>HomeShark, Inc.</strong>&nbsp;·&nbsp;homeshark.com&nbsp;·&nbsp;Effective Date: March 10, 2026
      </div>
    </header>

    <!-- Content -->
    <div class="pp-content">

      <!-- At a glance -->
      <div class="pp-alert pp-alert--info">
        <div class="pp-alert__title">At a Glance — Our Core Privacy Commitments</div>
        <p>HomeShark does not sell your personal information for monetary compensation and does not use your data for
          third-party advertising purposes. We collect only what is necessary to provide our services. You can access,
          correct, or delete your data at any time. We delete or anonymize most personal data within 30 days of account
          closure, except where retention is required for legal, regulatory, or transaction-related purposes. We use
          essential and analytics cookies only.</p>
      </div>

      <!-- 1. Introduction -->
      <h2 class="pp-h1" id="introduction">1. Introduction</h2>
      <p>HomeShark, Inc. (<strong>"HomeShark," "we," "us,"</strong> or <strong>"our"</strong>) operates the real estate
        technology platform at homeshark.com (the <strong>"Platform"</strong>). This Privacy Policy explains how we
        collect, use, disclose, and safeguard your personal information when you visit or use the Platform.</p>
      <p>This Privacy Policy applies to all unauthenticated visitors, registered users, and any other persons who access
        or use the Platform, regardless of whether they have created an account. By accessing or using the Platform, you
        acknowledge that you have read and understood this Privacy Policy. If you do not agree with Policy terms
        described herein, you must discontinue your use of the Platform.</p>
      <p>This Privacy Policy is incorporated by reference into HomeShark's Terms of Service. Capitalized terms not
        defined in this Privacy Policy have the meanings given to them in the Terms of Service, available at <strong>homeshark.com/terms</strong>.
      </p>
      <p><strong>We do not sell, rent, or trade your personal information to third parties for marketing or advertising
        purposes.</strong> HomeShark does not use your data to serve you targeted advertising or retargeting campaigns
        on other platforms.</p>

      <!-- 2. Information We Collect -->
      <h2 class="pp-h1" id="information-we-collect">2. Information We Collect</h2>
      <p>We collect information in three ways: information you provide directly, information collected automatically,
        and information received from third parties.</p>

      <h3 class="pp-h2" id="information-you-provide-directly">2.1 Information You Provide Directly</h3>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Category</th>
            <th>Specific Data Points</th>
            <th>When Collected</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Identity &amp; Contact</td>
            <td>First name, last name, email address, phone number</td>
            <td>At account registration</td>
          </tr>
          <tr>
            <td>Verification Data</td>
            <td>SMS one-time passcode (OTP), phone number used for verification</td>
            <td>During SMS verification flow</td>
          </tr>
          <tr>
            <td>Account Preferences</td>
            <td>Buyer type (first-time, repeat, investor), notification preferences, communication frequency</td>
            <td>During onboarding wizard</td>
          </tr>
          <tr>
            <td>Property Activity</td>
            <td>Saved searches, favorited properties, showing requests, search filter history</td>
            <td>During active Platform use</td>
          </tr>
          <tr>
            <td>Contract &amp; Offer Data</td>
            <td>Offer price, earnest money, contingency elections, closing date preferences, and all other fields
              completed in the Contract Builder
            </td>
            <td>When using the Contract Builder</td>
          </tr>
          <tr>
            <td>Payment &amp; Billing</td>
            <td>Subscription plan selection, transaction type; payment card details processed and stored by our payment
              processor (Stripe) — HomeShark does not store full card numbers
            </td>
            <td>At subscription enrollment or flat-fee transaction</td>
          </tr>
          <tr>
            <td>Communications</td>
            <td>Messages, support requests, feedback, and correspondence submitted to HomeShark</td>
            <td>When you contact us</td>
          </tr>
          </tbody>
        </table>
      </div>

      <h3 class="pp-h2" id="information-collected-automatically">2.2 Information Collected Automatically</h3>
      <p>When you access the Platform, we automatically collect certain technical and usage information through cookies
        and similar technologies:</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Category</th>
            <th>Specific Data Points</th>
            <th>Purpose</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Device &amp; Browser</td>
            <td>IP address, browser type and version, operating system, device type, screen resolution</td>
            <td>Security, fraud prevention, technical support</td>
          </tr>
          <tr>
            <td>Usage &amp; Analytics</td>
            <td>Pages visited, features used, search queries entered, time spent on pages, click patterns, referral
              source
            </td>
            <td>Platform improvement, analytics</td>
          </tr>
          <tr>
            <td>Session Data</td>
            <td>Login timestamps, session duration, authentication tokens</td>
            <td>Security and account integrity</td>
          </tr>
          <tr>
            <td>Performance Data</td>
            <td>Page load times, error logs, crash reports</td>
            <td>Platform reliability and bug fixes</td>
          </tr>
          </tbody>
        </table>
      </div>

      <h3 class="pp-h2" id="information-from-third-parties">2.3 Information from Third Parties</h3>
      <p>We may receive limited information about you from third-party service providers in the course of delivering our
        services:</p>
      <ul>
        <li><p>Payment processor (Stripe): Transaction confirmation status and fraud signals. We do not receive your
          full payment card number.</p></li>
        <li><p>E-signature provider (DocuSign or equivalent, as necessary to facilitate legally binding agreements
          requested by you): Signature completion status, timestamp, and audit trail data for executed contracts and
          brokerage agreements.</p></li>
        <li><p>SMS/email delivery providers: Delivery status and bounce notifications for messages sent to you.</p></li>
        <li><p>Analytics platforms (Google Analytics): Pseudonymous and aggregated (with IP anonymization enabled where
          applicable) usage metrics to help us understand how users interact with the Platform.</p></li>
      </ul>

      <!-- 3. How We Use -->
      <h2 class="pp-h1" id="how-we-use-your-information">3. How We Use Your Information</h2>
      <p>HomeShark uses your personal information only as necessary to provide and improve the Platform and to comply
        with our legal obligations. We do not use your personal information for advertising or marketing to third
        parties.</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Purpose</th>
            <th>Data Used</th>
            <th>Legal Basis</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Provide and operate the Platform</td>
            <td>All categories</td>
            <td>Contract performance</td>
          </tr>
          <tr>
            <td>Account registration and authentication</td>
            <td>Identity, contact, verification data</td>
            <td>Contract performance</td>
          </tr>
          <tr>
            <td>Personalize your experience and dashboard</td>
            <td>Buyer type, preferences, search history</td>
            <td>Contract performance / Legitimate interest</td>
          </tr>
          <tr>
            <td>Process payments and manage subscriptions</td>
            <td>Payment and billing data</td>
            <td>Contract performance</td>
          </tr>
          <tr>
            <td>Generate and manage real estate contracts</td>
            <td>Contract and offer data, identity data</td>
            <td>Contract performance / Legal obligation</td>
          </tr>
          <tr>
            <td>Facilitate showing requests</td>
            <td>Identity, contact, property activity data</td>
            <td>Contract performance</td>
          </tr>
          <tr>
            <td>Send transactional communications</td>
            <td>Contact info, notification preferences</td>
            <td>Contract performance</td>
          </tr>
          <tr>
            <td>Send service and account notifications</td>
            <td>Contact info, notification preferences</td>
            <td>Legitimate interest</td>
          </tr>
          <tr>
            <td>Analyze and improve Platform performance</td>
            <td>Usage, analytics, device/browser data</td>
            <td>Legitimate interest</td>
          </tr>
          <tr>
            <td>Prevent fraud and ensure security</td>
            <td>Device/browser, session, identity data</td>
            <td>Legitimate interest / Legal obligation</td>
          </tr>
          <tr>
            <td>Comply with Georgia real estate law and GREC requirements</td>
            <td>Identity, contract, brokerage agreement data</td>
            <td>Legal obligation</td>
          </tr>
          <tr>
            <td>Respond to your inquiries and support requests</td>
            <td>Communications, identity data</td>
            <td>Legitimate interest</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="pp-callout-nomarket">
        <strong>No Marketing Use of Your Data</strong>
        <p>HomeShark does not use your personal information to send unsolicited promotional messages to third parties,
          sell your data to data brokers, serve you targeted advertising on other platforms, or build advertising
          profiles. The only marketing-related communications we send are HomeShark's own service announcements and, if
          you opt in, HomeShark feature updates — which you may opt out of at any time.</p>
      </div>

      <!-- 4. Cookies -->
      <h2 class="pp-h1" id="cookies-tracking-technologies">4. Cookies &amp; Tracking Technologies</h2>

      <h3 class="pp-h2" id="types-of-cookies-we-use">4.1 Types of Cookies We Use</h3>
      <p>HomeShark uses two categories of cookies and similar tracking technologies on the Platform:</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Cookie Type</th>
            <th>Description &amp; Purpose</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Essential Cookies</td>
            <td>Strictly necessary for the Platform to function. These include session management cookies (keeping you
              logged in), security tokens, CSRF protection tokens, and user preference cookies. These cookies cannot be
              disabled without breaking core Platform functionality.
            </td>
          </tr>
          <tr>
            <td>Analytics Cookies</td>
            <td>Used to understand how visitors interact with the Platform. We use Google Analytics to collect
              pseudonymous and aggregated usage data (with IP anonymization enabled where applicable) about page views,
              feature usage, traffic sources, and user journeys. This helps us improve the Platform. Analytics cookies
              do not personally identify you.
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <h3 class="pp-h2" id="what-we-do-not-use">4.2 What We Do Not Use</h3>
      <p>HomeShark does not use third-party advertising cookies, retargeting pixels, social media tracking pixels (such
        as Facebook Pixel or Twitter Pixel), or cross-site tracking technologies designed to build advertising profiles
        or track your activity across third-party websites.</p>

      <h3 class="pp-h2" id="managing-cookies">4.3 Managing Cookies</h3>
      <p>You can control and manage cookies through your browser settings. Most browsers allow you to refuse cookies,
        delete existing cookies, or be notified when a cookie is set. Please note that disabling essential cookies will
        impair the functionality of the Platform, including your ability to stay logged in or use secure features.</p>
      <p>For information about managing Google Analytics specifically, you may install the Google Analytics Opt-Out
        Browser Add-on available at tools.google.com/dlpage/gaoptout.</p>

      <!-- 5. Sharing -->
      <h2 class="pp-h1" id="how-we-share-your-information">5. How We Share Your Information</h2>
      <p><strong>HomeShark does not sell your personal information.</strong> We share your information only in the
        limited circumstances described below, and only to the extent necessary to provide our services or comply with
        legal obligations.</p>

      <h3 class="pp-h2" id="service-providers">5.1 Service Providers</h3>
      <p>We share your information with carefully selected third-party service providers who assist us in operating the
        Platform. These providers are contractually obligated to use your information only as directed by HomeShark and
        in accordance with this Privacy Policy:</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Provider Category</th>
            <th>Provider (Example)</th>
            <th>Data Shared &amp; Purpose</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Payment Processing</td>
            <td>Stripe, Inc.</td>
            <td>Payment card data (tokenized), transaction amounts, billing details. Used to process subscription
              payments and flat-fee transactions.
            </td>
          </tr>
          <tr>
            <td>Electronic Signatures</td>
            <td>DocuSign / HelloSign</td>
            <td>Name, email, contract content. Used to facilitate legally binding e-signatures on contracts and
              brokerage agreements.
            </td>
          </tr>
          <tr>
            <td>SMS Delivery</td>
            <td>Twilio or equivalent</td>
            <td>Phone number, message content. Used to send verification OTPs and showing confirmation alerts.</td>
          </tr>
          <tr>
            <td>Email Delivery</td>
            <td>SendGrid / Postmark</td>
            <td>Email address, message content. Used to send account notifications, showing confirmations, and
              transactional emails.
            </td>
          </tr>
          <tr>
            <td>Analytics</td>
            <td>Google Analytics</td>
            <td>Anonymized usage data, IP address (anonymized). Used to analyze Platform usage and improve features.
            </td>
          </tr>
          <tr>
            <td>Cloud Hosting &amp; Storage</td>
            <td>AWS or equivalent</td>
            <td>All Platform data stored in encrypted form. Used to host the Platform and store user data securely.</td>
          </tr>
          </tbody>
        </table>
      </div>

      <h3 class="pp-h2" id="real-estate-transaction-parties">5.2 Real Estate Transaction Parties</h3>
      <p>When you initiate the Contract Builder and authorize HomeShark to submit an offer on your behalf, we will share
        the relevant contract and contact information with the listing agent and/or seller as required to present your
        offer. This sharing is a core part of the brokerage services you have engaged HomeShark to provide and is not a
        sale of your data.</p>

      <h3 class="pp-h2" id="mls-organizations">5.3 MLS Organizations</h3>
      <p>Your use of the Platform is subject to VOW (Virtual Office Website) data licensing agreements with FMLS and
        GAMLS. These MLS organizations may require that HomeShark registered user accounts be disclosed to them upon
        request for compliance and audit purposes as a condition of HomeShark's MLS data access and compliance
        obligations. HomeShark will cooperate with such requests as required by its MLS membership obligations.</p>

      <h3 class="pp-h2" id="legal-compliance-protection">5.4 Legal Compliance &amp; Protection</h3>
      <p>We may disclose your information to third parties when we believe in good faith that disclosure is necessary
        to:</p>
      <ul>
        <li><p>Comply with applicable law, regulation, legal process, or enforceable governmental request, including
          requests from the Georgia Real Estate Commission (GREC).</p></li>
        <li><p>Enforce our Terms of Service or other agreements.</p></li>
        <li><p>Detect, investigate, or prevent fraud, security incidents, or other potentially illegal activity.</p>
        </li>
        <li><p>Protect the rights, property, or safety of HomeShark, our users, or the public.</p></li>
      </ul>

      <h3 class="pp-h2" id="business-transfers">5.5 Business Transfers</h3>
      <p>In the event that HomeShark is involved in a merger, acquisition, financing, reorganization, bankruptcy, or
        sale of company assets, your information may be transferred as part of that transaction. We will notify you via
        email and/or a prominent notice on the Platform before your personal information is transferred and becomes
        subject to a different privacy policy.</p>

      <!-- 6. Retention -->
      <h2 class="pp-h1" id="data-retention">6. Data Retention</h2>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Data Category</th>
            <th>Retention Period</th>
            <th>Reason</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Active account data</td>
            <td>Duration of account</td>
            <td>Necessary to provide Platform services</td>
          </tr>
          <tr>
            <td>Property search history &amp; saved searches</td>
            <td>Duration of account, deleted within 30 days of closure</td>
            <td>Personalization; no longer needed after closure</td>
          </tr>
          <tr>
            <td>Contract &amp; offer data</td>
            <td>7 years from transaction date</td>
            <td>Georgia real estate record-keeping requirements under O.C.G.A. §43-40-18</td>
          </tr>
          <tr>
            <td>Brokerage agreements &amp; disclosures</td>
            <td>7 years from execution date</td>
            <td>GREC compliance and legal record-keeping</td>
          </tr>
          <tr>
            <td>Payment &amp; billing records</td>
            <td>7 years from transaction date</td>
            <td>Tax, accounting, and legal compliance</td>
          </tr>
          <tr>
            <td>Communication records</td>
            <td>3 years from date of communication</td>
            <td>Dispute resolution and compliance</td>
          </tr>
          <tr>
            <td>Analytics &amp; usage data</td>
            <td>26 months (Google Analytics default)</td>
            <td>Platform improvement; anonymized</td>
          </tr>
          <tr>
            <td>Security &amp; fraud logs</td>
            <td>12 months</td>
            <td>Security incident investigation</td>
          </tr>
          <tr>
            <td>All other personal data</td>
            <td>Deleted within 30 days of account closure</td>
            <td>Privacy by default</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="pp-alert pp-alert--info">
        <div class="pp-alert__title">Account Closure &amp; Data Deletion</div>
        <p>When you close your HomeShark account, we will delete or anonymize all of your personal data within 30 days,
          except where we are required to retain certain records by law (such as executed real estate contracts,
          brokerage agreements, and financial transaction records, which must be retained for 7 years under Georgia
          law). You will receive a confirmation email when your deletion request has been completed.</p>
      </div>

      <!-- 7. Rights -->
      <h2 class="pp-h1" id="your-privacy-rights">7. Your Privacy Rights</h2>
      <p>HomeShark respects your right to control your personal information. You have the following rights with respect
        to your data:</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Right</th>
            <th>Description</th>
            <th>How to Exercise</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Right to Access</td>
            <td>You may request a copy of all personal information HomeShark holds about you, including the categories
              of data collected, the purposes for which it is used, and the third parties with whom it is shared.
            </td>
            <td>Submit a request to <a href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a> or through your
              account settings.
            </td>
          </tr>
          <tr>
            <td>Right to Correct</td>
            <td>You may request that we correct any inaccurate or incomplete personal information we hold about you. You
              may also update most information directly through your account profile.
            </td>
            <td>Update via account settings or contact <a
              href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a>.
            </td>
          </tr>
          <tr>
            <td>Right to Delete</td>
            <td>You may request deletion of your personal information. We will delete your data within 30 days of your
              verified request, subject to our legal retention obligations described in Section 6.
            </td>
            <td>Submit a deletion request at <a href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a> or
              through account settings &gt; Delete Account.
            </td>
          </tr>
          <tr>
            <td>Right to Opt Out of Marketing</td>
            <td>Although HomeShark does not currently send promotional marketing communications to third parties or use
              your data for advertising, you may opt out of any HomeShark service-related announcements or feature
              update emails at any time.
            </td>
            <td>Click 'Unsubscribe' in any email, or update your notification preferences in account settings.</td>
          </tr>
          <tr>
            <td>Right to Data Portability</td>
            <td>You may request a machine-readable export of your personal data that you have provided to HomeShark.
            </td>
            <td>Submit a request to <a href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a>. We will
              respond within 30 days.
            </td>
          </tr>
          <tr>
            <td>Right to Restrict Processing</td>
            <td>In certain circumstances, you may request that we restrict the processing of your personal information
              while a dispute or correction request is pending.
            </td>
            <td>Contact <a href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a> with the specific
              restriction requested.
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <p>We will respond to all verified privacy rights requests within thirty (30) days of receipt. We may need to
        verify your identity before fulfilling your request. We will not discriminate against you for exercising any of
        the rights described above.</p>
      <p>Please note that certain rights may be limited where HomeShark has a legal obligation to retain data (such as
        executed real estate contracts and brokerage records required under Georgia law) or where fulfilling the request
        would adversely affect the rights of other individuals.</p>

      <!-- 8. Security -->
      <h2 class="pp-h1" id="data-security">8. Data Security</h2>
      <p>HomeShark implements industry-standard technical and organizational security measures designed to protect your
        personal information from unauthorized access, disclosure, alteration, and destruction. Our security measures
        include:</p>
      <ul>
        <li><p>Encryption of all data in transit using TLS 1.3 or higher.</p></li>
        <li><p>Encryption of all personal data at rest using AES-256 encryption.</p></li>
        <li><p>Access controls limiting employee access to personal data on a need-to-know basis.</p></li>
        <li><p>Multi-factor authentication requirements for administrative access to Platform systems.</p></li>
        <li><p>Regular security assessments and vulnerability scanning.</p></li>
        <li><p>Secure, encrypted storage of all contract documents and brokerage agreements.</p></li>
        <li><p>Payment card data handled exclusively by PCI-DSS compliant payment processor (Stripe); HomeShark does not
          store full payment card numbers.</p></li>
      </ul>
      <p>Despite these measures, no method of electronic storage or transmission over the internet is fully secure.
        HomeShark cannot guarantee the absolute security of your information. In the event of a data breach that affects
        your personal information, we will notify you as required by applicable law, including the Georgia Personal
        Identity Protection Act (O.C.G.A. § 10-1-910 et seq.)</p>

      <!-- 9. Children -->
      <h2 class="pp-h1" id="childrens-privacy">9. Children's Privacy</h2>
      <p>The Platform is not directed to children under the age of eighteen (18) and we do not knowingly collect
        personal information from anyone under eighteen (18). If you are under eighteen (18), please do not use the
        Platform or submit any personal information to us.</p>
      <p>If we become aware that we have inadvertently collected personal information from a child under eighteen (18),
        we will take steps to delete that information as promptly as possible. If you believe we may have collected
        information from a child under eighteen (18), please contact us immediately at <a
          href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a>.</p>

      <!-- 10. Georgia -->
      <h2 class="pp-h1" id="georgia-specific-disclosures">10. Georgia-Specific Disclosures</h2>

      <h3 class="pp-h2" id="georgia-personal-identity-protection-act">10.1 Georgia Personal Identity Protection Act</h3>
      <p>HomeShark complies with the Georgia Personal Identity Protection Act (O.C.G.A. § 10-1-910 et seq.). In the
        event of a security breach that results in the unauthorized acquisition of your unencrypted personal
        information, we will notify you in the most expedient time possible and without unreasonable delay, as required
        by Georgia law.</p>

      <h3 class="pp-h2" id="georgia-real-estate-commission-grec-records">10.2 Georgia Real Estate Commission (GREC)
        Records</h3>
      <p>As a licensed Georgia real estate brokerage, HomeShark is required by O.C.G.A. Title 43, Chapter 40 and
        applicable GREC regulations to maintain certain transaction records, including executed buyer brokerage
        agreements, purchase and sale contracts, and related correspondence, for a minimum of three (3) years (and we
        retain these for seven (7) years as a best practice). These records may be subject to inspection by GREC and
        cannot be deleted upon user request.</p>

      <h3 class="pp-h2" id="mls-data-obligations">10.3 MLS Data Obligations</h3>
      <p>Your registered account information may be subject to disclosure to FMLS and GAMLS pursuant to our MLS
        membership agreements and VOW data licensing terms. These disclosures are required for HomeShark's continued MLS
        membership and data access, which is fundamental to the Platform's operation.</p>

      <!-- 11. Third Party -->
      <h2 class="pp-h1" id="third-party-links-services">11. Third-Party Links &amp; Services</h2>
      <p>The Platform may contain links to third-party websites or services, including MLS property listing pages,
        mortgage calculators, or lender resources. This Privacy Policy applies only to the HomeShark Platform. We are
        not responsible for the privacy practices of any third-party websites or services, and we encourage you to
        review the privacy policies of any third-party services you access through or in connection with the
        Platform.</p>

      <!-- 12. Changes -->
      <h2 class="pp-h1" id="changes-to-this-privacy-policy">12. Changes to This Privacy Policy</h2>
      <p>HomeShark reserves the right to update this Privacy Policy at any time. We will notify you of material changes
        by:</p>
      <ul>
        <li><p>Posting the revised Privacy Policy on the Platform with an updated effective date.</p></li>
        <li><p>Sending an email notification to the address associated with your account for significant changes.</p>
        </li>
        <li><p>Displaying a prominent notice on the Platform for at least thirty (30) days following a material
          change.</p></li>
      </ul>
      <p>Your continued use of the Platform after the effective date of any revised Privacy Policy constitutes your
        acceptance of the changes. If you do not agree with a revised Privacy Policy, you must stop using the Platform
        and may request deletion of your account.</p>

      <!-- 13. Contact -->
      <h2 class="pp-h1" id="contact-us-privacy-inquiries">13. Contact Us — Privacy Inquiries</h2>
      <p>If you have questions, concerns, or requests regarding this Privacy Policy or HomeShark's privacy practices,
        please contact our Privacy team:</p>
      <div class="pp-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Contact Method</th>
            <th>Details</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Privacy Email</td>
            <td><a href="mailto:privacy@homeshark.com">privacy&#64;homeshark.com</a></td>
          </tr>
          <tr>
            <td>General Support</td>
            <td><a href="mailto:support@homeshark.com">support&#64;homeshark.com</a></td>
          </tr>
          <tr>
            <td>Legal Inquiries</td>
            <td><a href="mailto:legal@homeshark.com">legal&#64;homeshark.com</a></td>
          </tr>
          <tr>
            <td>Website</td>
            <td>homeshark.com/privacy</td>
          </tr>
          <tr>
            <td>Company</td>
            <td>HomeShark, Inc.</td>
          </tr>
          <tr>
            <td>Jurisdiction</td>
            <td>State of Georgia, Fulton County, United States</td>
          </tr>
          <tr>
            <td>GA Broker License</td>
            <td>271410</td>
          </tr>
          <tr>
            <td>Response Time</td>
            <td>Within 30 days of verified request</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div><!-- /pp-content -->
  </main><!-- /pp-main -->
</div><!-- /pp-layout -->
